import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { semesterReviewService } from "../../services";

const STATUS_CONFIG = {
  DRAFT: {
    label: "Draft",
    pill: "bg-gray-100 text-gray-600 border-gray-200",
    dot: "bg-gray-400",
  },
  IN_REVIEW: {
    label: "In Progress",
    pill: "bg-blue-50 text-blue-700 border-blue-200",
    dot: "bg-blue-500",
  },
  COMPLETED: {
    label: "Completed",
    pill: "bg-green-50 text-green-700 border-green-200",
    dot: "bg-green-500",
  },
};

const TEMPLATE_CONFIG = {
  KOMPETENZEN: { label: "Bildungsbericht", icon: "fa-clipboard-check" },
  BEWERTUNG_DER_AUSBILDUNG: {
    label: "Bewertung der Ausbildung",
    icon: "fa-comments",
  },
  BB_LEISTUNGSBEURTEILUNG: {
    label: "Leistungsbeurteilung",
    icon: "fa-chart-line",
  },
};

/**
 * Orders Swiss half-year semesters on a single scale so they can be compared.
 * HS runs August-January, FS February-July, so HS25 < FS26 < HS26.
 */
function semesterIndex(isHerbst, year) {
  return isHerbst ? year * 2 : (year - 1) * 2 + 1;
}

function currentSemesterIndex(now = new Date()) {
  const month = now.getMonth() + 1;
  if (month >= 8) return semesterIndex(true, now.getFullYear());
  if (month === 1) return semesterIndex(true, now.getFullYear() - 1);
  return semesterIndex(false, now.getFullYear());
}

/**
 * Which semester a review belongs to. Prefers an explicit HSyy/FSyy code from
 * the semester label or title; falls back to the date it was completed or
 * created, so reviews without a linked semester are still placed.
 */
function reviewSemesterIndex(review) {
  const source = `${review.semesterLabel || ""} ${review.title || ""}`;
  const match = source.match(/\b(HS|FS)\s?(\d{2})(\d{2})?\b/i);
  if (match) {
    const isHerbst = match[1].toUpperCase() === "HS";
    const year = match[3]
      ? Number(`${match[2]}${match[3]}`)
      : 2000 + Number(match[2]);
    return semesterIndex(isHerbst, year);
  }

  const raw = review.completedAt || review.createdAt;
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return currentSemesterIndex(date);
}

function semesterChipLabel(review) {
  if (review.semesterLabel) return review.semesterLabel;
  const match = `${review.title || ""}`.match(/\b(HS|FS)\s?\d{2,4}\b/i);
  return match ? match[0].toUpperCase() : null;
}

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString("de-CH");
}

const LearnerSemesterReviewWidget = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const data = await semesterReviewService.getMyReviews();
      setReviews(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  // Only the current and the previous semester are relevant here. Everything
  // older stays reachable through "View all".
  const recentReviews = useMemo(() => {
    const cutoff = currentSemesterIndex() - 1;
    return reviews
      .filter((review) => {
        const index = reviewSemesterIndex(review);
        // Undated reviews are kept rather than silently dropped.
        return index == null || index >= cutoff;
      })
      .sort((a, b) => {
        const diff = (reviewSemesterIndex(b) ?? 0) - (reviewSemesterIndex(a) ?? 0);
        if (diff !== 0) return diff;
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      });
  }, [reviews]);

  const hiddenCount = reviews.length - recentReviews.length;

  const handleReviewClick = (reviewId) => {
    navigate(`/semester-review/${reviewId}`);
  };

  if (loading) {
    return (
      <div className="card-brand p-6">
        <h2 className="text-xl font-bold text-grey-dusk mb-4">
          My Semester Reviews
        </h2>
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-[72px] rounded-xl bg-pale-dawn/40 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card-brand p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="text-xl font-bold text-grey-dusk">
          My Semester Reviews
        </h2>
        <button
          onClick={() => navigate("/learner/semester-reviews")}
          className="text-sm font-semibold text-sunrise-red hover:text-sunset-orange transition-colors flex items-center gap-1.5"
        >
          View all
          <i className="fas fa-arrow-right text-xs"></i>
        </button>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Current and previous semester
        {hiddenCount > 0 && ` · ${hiddenCount} older hidden`}
      </p>

      {recentReviews.length === 0 ? (
        <div className="text-center py-10 px-4 rounded-xl border border-dashed border-pale-dawn bg-ice-white/50">
          <i className="fas fa-inbox text-3xl text-gray-300 mb-3 block"></i>
          <p className="text-sm font-semibold text-grey-dusk">
            {reviews.length === 0
              ? "No reviews yet"
              : "Nothing from this or last semester"}
          </p>
          {reviews.length > 0 && (
            <button
              onClick={() => navigate("/learner/semester-reviews")}
              className="mt-2 text-xs font-semibold text-sunrise-red hover:underline"
            >
              See all {reviews.length} reviews
            </button>
          )}
        </div>
      ) : (
        <ul className="space-y-2">
          {recentReviews.map((review) => {
            const status =
              STATUS_CONFIG[review.status] || STATUS_CONFIG.DRAFT;
            const template = TEMPLATE_CONFIG[review.templateCode] || {
              label: review.templateCode,
              icon: "fa-file-lines",
            };
            const semesterChip = semesterChipLabel(review);
            const created = formatDate(review.createdAt);
            const completed = formatDate(review.completedAt);
            const unread =
              review.status === "COMPLETED" && review.isReadByLearner === false;

            return (
              <li key={review.id}>
                <button
                  type="button"
                  onClick={() => handleReviewClick(review.id)}
                  className="w-full text-left rounded-xl border border-pale-dawn bg-white px-4 py-3 hover:border-sunrise-red/40 hover:bg-ice-white/60 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 w-9 h-9 shrink-0 rounded-lg bg-sunrise-red/10 text-sunrise-red flex items-center justify-center">
                      <i className={`fas ${template.icon} text-sm`}></i>
                    </span>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <h4 className="font-semibold text-grey-dusk leading-snug truncate group-hover:text-sunrise-red transition-colors">
                          {review.title}
                        </h4>
                        <span
                          className={`shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${status.pill}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${status.dot}`}
                          />
                          {status.label}
                        </span>
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                        <span className="text-gray-600">{template.label}</span>
                        {semesterChip && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className="px-1.5 py-0.5 rounded bg-pale-dawn/60 text-grey-dusk font-medium">
                              {semesterChip}
                            </span>
                          </>
                        )}
                        {unread && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className="px-1.5 py-0.5 rounded bg-sunrise-red/10 text-sunrise-red font-semibold">
                              New
                            </span>
                          </>
                        )}
                      </div>

                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 text-[11px] text-gray-400">
                        {created && <span>Created {created}</span>}
                        {completed && <span>Completed {completed}</span>}
                      </div>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LearnerSemesterReviewWidget;
